/**
 * Service for expense form business logic and data transformations
 */
import {
  CreateExpenseDto,
  GroupResponseDto,
  PendingConversionFields,
  PlaceholderClaimSummaryDto,
  UpdateExpenseDto,
  getCurrencyDecimals,
} from '@splitfiesta/shared';

import { parseDate } from '../../utils/dateUtils';
import { normalizeDecimalInput } from '../../utils/formatUtils';
import { extractMemberData } from '../../utils/memberUtils';
import { PlaceholderMemberService } from '../group/PlaceholderMemberService';
import {
  ConversionSnapshot,
  conversionFieldsForCreate,
  conversionFieldsForUpdate,
  pendingFieldsForQueuedEdit,
  storedRateForEdit,
} from './ConversionService';
import { splitCalculationService } from './SplitCalculationService';

export interface Split {
  userId: string;
  userName: string;
  profileImage?: string;
  amount?: number;
  amountFormatted?: string;
  percentage?: number;
  shares?: number; // Per-expense share override
  defaultShares?: number; // Member's default shares from group config
  included: boolean;
  isPlaceholder?: boolean;
  /**
   * Set on the placeholder half of an unresolved claim whose claimant is also
   * in the group: the name to show in the "pending" hint. Display only — the
   * row stays pickable.
   */
  claimPendingFor?: string;
}

export interface Payer {
  userId: string;
  amount: number;
}

/**
 * The group payload the expense form reads: `GroupResponseDto` plus the nested
 * member rows the web/mobile query layers build, including the placeholder and
 * claim fields the pickers act on.
 */
export interface ExtendedGroup extends GroupResponseDto {
  members?: Array<{
    userId: { _id: string; name: string };
    isPlaceholder?: boolean;
    claim?: PlaceholderClaimSummaryDto;
  }>;
}

export interface ExpenseFormData {
  title: string;
  /** Group-currency amount. */
  numericAmount: number;
  /** Group currency. */
  currency: string;
  category: string;
  subcategory?: string;
  date: string;
  notes: string;
  splitMethod: 'equal' | 'percentage' | 'shares' | 'custom';
  paidBy: Payer[];
  splits: Split[];
  /** The group's currency — same value as `currency` above. */
  groupCurrency: string;
  /** What the user typed, in `enteredCurrency`. */
  enteredAmount: number;
  enteredCurrency: string;
  /**
   * True when a foreign entry has no rate available offline: the expense is
   * queued in `enteredCurrency` and converted at sync time instead of now.
   */
  pendingConversion: boolean;
  /** Snapshot of the current conversion state, or null for a same-currency expense. */
  conversionSnapshot: ConversionSnapshot | null;
}

class ExpenseFormService {
  /**
   * Keyed by the id `extractMemberData` resolves — the group payload nests the
   * id under `userId`, while the core rule works on flat rows.
   */
  private pendingClaimNames(group: ExtendedGroup): Map<string, string> {
    return PlaceholderMemberService.pendingClaimNames(
      (group.members ?? []).map((member, index) => ({
        userId: extractMemberData(member, index).id,
        isPlaceholder: member.isPlaceholder,
        claim: member.claim,
      }))
    );
  }

  /**
   * Get local date in YYYY-MM-DD format
   */
  getLocalDateString(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Initialize splits from group members with optional default config
   * Auto-deselects members with 0% when using percentage split method
   */
  initializeSplitsFromGroup(
    group: ExtendedGroup,
    defaultSplitConfig?: {
      percentages?: Record<string, number>;
      shares?: Record<string, number>;
    },
    defaultSplitMethod?: string
  ): Split[] {
    if (!group.members) return [];

    const pendingClaims = this.pendingClaimNames(group);

    return group.members.map((member, index) => {
      const memberData = extractMemberData(member, index);
      const userId = memberData.id;
      const claimPendingFor = pendingClaims.get(userId);

      // Get default percentage if configured
      const defaultPercentage = defaultSplitConfig?.percentages?.[userId];
      // Get default shares if configured
      const defaultShares = defaultSplitConfig?.shares?.[userId];

      // Auto-deselect members with 0% when using percentage split method
      // This prevents the confusing "selected but pays $0" state
      const shouldInclude =
        defaultSplitMethod === 'percentage'
          ? defaultPercentage !== undefined && defaultPercentage > 0
          : true;

      return {
        userId,
        userName: memberData.name,
        profileImage: memberData.profileImage,
        amount: undefined,
        amountFormatted: undefined,
        percentage: defaultPercentage,
        shares: defaultShares,
        defaultShares: defaultShares,
        // The placeholder half of an unresolved claim is the same person as
        // the claimant, so pre-selecting both would charge them twice.
        included: shouldInclude && claimPendingFor === undefined,
        isPlaceholder: member.isPlaceholder,
        claimPendingFor,
      };
    });
  }

  /**
   * Initialize splits from expense data for edit mode
   */
  initializeSplitsFromExpense(
    group: ExtendedGroup,
    expenseData: unknown
  ): Split[] {
    if (!group.members) return [];

    const pendingClaims = this.pendingClaimNames(group);

    return group.members.map((member, index) => {
      const memberData = extractMemberData(member, index);
      const memberId = memberData.id;

      const splits = (expenseData as { splits?: unknown[] })?.splits;
      const existingSplit = splits?.find((s: unknown) => {
        if (typeof s !== 'object' || s === null) return false;
        const split = s as { userId?: unknown };
        const splitUserId =
          typeof split.userId === 'string'
            ? split.userId
            : (split.userId as { _id?: string; id?: string })._id ||
              (split.userId as { _id?: string; id?: string }).id;
        return splitUserId === memberId;
      });

      const amount = (existingSplit as { amount?: number })?.amount || 0;
      const shares = (existingSplit as { shares?: number })?.shares;
      return {
        userId: memberId,
        userName: memberData.name,
        profileImage: memberData.profileImage,
        amount,
        amountFormatted:
          amount > 0
            ? amount.toFixed(
                getCurrencyDecimals(
                  (expenseData as { currency?: string })?.currency || 'USD'
                )
              )
            : '',
        percentage: (existingSplit as { percentage?: number })?.percentage || 0,
        shares,
        // An expense being edited keeps the selection it was saved with — a
        // pending claim only adds the hint.
        included: existingSplit ? true : false,
        isPlaceholder: member.isPlaceholder,
        claimPendingFor: pendingClaims.get(memberId),
      };
    });
  }

  /**
   * Initialize default payer (current user)
   */
  initializeDefaultPayer(userId: string): Payer[] {
    return [{ userId, amount: 0 }];
  }

  /**
   * Check if payers are in default state for auto-update
   */
  shouldAutoUpdatePayer(paidBy: Payer[], userId: string): boolean {
    return (
      paidBy.length === 1 &&
      paidBy[0].userId === userId &&
      paidBy[0].amount === 0
    );
  }

  /**
   * Initialize payers from expense data
   */
  initializePayersFromExpense(
    expenseData: unknown,
    defaultUserId: string
  ): Payer[] {
    const expense = expenseData as { payers?: unknown[]; amount?: number };
    if (expense && expense.payers && Array.isArray(expense.payers)) {
      return expense.payers.map((payer: unknown) => {
        const payerObj = payer as { userId?: string; amount?: number };
        return {
          userId: payerObj.userId || '',
          amount: payerObj.amount || 0,
        };
      });
    } else {
      return [{ userId: defaultUserId, amount: expense?.amount || 0 }];
    }
  }

  /**
   * Prepare members data for MultiPayerSelect
   */
  prepareMembersForSelect(group: ExtendedGroup | null) {
    if (!group?.members) return [];

    const pendingClaims = this.pendingClaimNames(group);

    return group.members.map((member, index) => {
      const memberData = extractMemberData(member, index);
      return {
        userId: memberData.id,
        name: memberData.name,
        profileImage: memberData.profileImage,
        // `extractMemberData` only knows the identity fields, so the
        // placeholder flag has to be read off the raw member.
        isPlaceholder: member.isPlaceholder,
        claimPendingFor: pendingClaims.get(memberData.id),
      };
    });
  }

  /**
   * Validate expense form data
   */
  validateExpenseForm(
    formData: ExpenseFormData,
    t: (key: string, params?: Record<string, unknown>) => string
  ) {
    // Basic validation
    if (!formData.title.trim()) {
      throw new Error(t('expenses:errors.titleRequired'));
    }

    if (!formData.numericAmount || formData.numericAmount <= 0) {
      throw new Error(t('expenses:errors.invalidAmount'));
    }

    // Validate splits
    const splitValidation = splitCalculationService.validateSplits(
      formData.numericAmount,
      formData.splitMethod,
      formData.splits,
      formData.currency,
      t
    );

    if (!splitValidation.isValid) {
      throw new Error(splitValidation.errors.join(', '));
    }

    // Validate payers
    const payerValidation = splitCalculationService.validatePayers(
      formData.paidBy,
      formData.numericAmount,
      formData.currency,
      t
    );

    if (!payerValidation.isValid) {
      throw new Error(payerValidation.error || '');
    }
  }

  /**
   * Create expense DTO for API submission
   */
  createExpenseDto(
    formData: ExpenseFormData,
    groupId: string,
    displayAmounts: { [userId: string]: number }
  ): CreateExpenseDto & PendingConversionFields {
    // Pending-conversion expenses are queued — and their splits/payers
    // rounded — in the ENTERED currency (the sync runner converts to the
    // group currency later); every other expense rounds in the group
    // currency, same as `amount` below. Using `groupCurrency`
    // unconditionally here corrupted pending EUR splits to COP's 0-decimal
    // precision (23.6 -> 24).
    const amountsCurrency = formData.pendingConversion
      ? formData.enteredCurrency
      : formData.groupCurrency;

    const shared = {
      groupId,
      title: formData.title.trim(),
      category: formData.category || undefined,
      // Use || to convert empty string or undefined to null (JSON.stringify preserves null)
      subcategory: formData.subcategory || null,
      date: formData.date, // Keep as YYYY-MM-DD to avoid timezone issues
      notes: formData.notes.trim() || undefined,
      splitMethod: formData.splitMethod,
      idempotencyKey: crypto.randomUUID(),
      payers: splitCalculationService.createFinalPayerData(formData.paidBy),
      splits: splitCalculationService.createFinalSplitData(
        formData.splits,
        formData.splitMethod,
        displayAmounts,
        amountsCurrency
      ),
    };

    if (formData.pendingConversion) {
      // No rate was available offline: queue the expense in the original
      // currency and let the sync runner derive the conversion fields.
      return {
        ...shared,
        amount: splitCalculationService.roundAmount(
          formData.enteredAmount,
          amountsCurrency
        ),
        currency: formData.enteredCurrency,
        pendingConversion: true,
        targetCurrency: formData.groupCurrency,
      };
    }

    const conversion = formData.conversionSnapshot
      ? conversionFieldsForCreate(formData.conversionSnapshot)
      : null;

    return {
      ...shared,
      amount: splitCalculationService.roundAmount(
        formData.numericAmount,
        amountsCurrency
      ),
      currency: formData.groupCurrency,
      ...(conversion ?? {}),
    };
  }

  /**
   * Create update expense DTO for API submission
   */
  createUpdateExpenseDto(
    formData: ExpenseFormData,
    displayAmounts: { [userId: string]: number },
    existing?: {
      originalCurrency?: string;
      rateSource?: 'provider' | 'manual';
      exchangeRate?: number;
    }
  ): UpdateExpenseDto & PendingConversionFields {
    // Only a queued (local) expense reaches here pending: the forms block
    // it for a server expense, whose edits are never queued. It stays in
    // the entered currency, like `createExpenseDto` queues it.
    const pending = formData.pendingConversion;
    const amountsCurrency = pending
      ? formData.enteredCurrency
      : formData.groupCurrency;
    const conversion = pending
      ? pendingFieldsForQueuedEdit(formData.groupCurrency, existing ?? {})
      : formData.conversionSnapshot
        ? conversionFieldsForUpdate(formData.conversionSnapshot, existing ?? {})
        : {};

    return {
      title: formData.title.trim(),
      amount: splitCalculationService.roundAmount(
        pending ? formData.enteredAmount : formData.numericAmount,
        amountsCurrency
      ),
      currency: amountsCurrency,
      payers: splitCalculationService.createFinalPayerData(formData.paidBy),
      splitMethod: formData.splitMethod,
      splits: splitCalculationService.createFinalSplitData(
        formData.splits,
        formData.splitMethod,
        displayAmounts,
        amountsCurrency
      ),
      category: formData.category || undefined,
      // Use || to convert empty string or undefined to null (JSON.stringify preserves null)
      subcategory: formData.subcategory || null,
      date: formData.date, // Keep as YYYY-MM-DD to avoid timezone issues
      // '' (not absent) so a cleared note reaches the patch.
      notes: formData.notes.trim(),
      ...conversion,
    };
  }

  /**
   * Format amount for currency display
   */
  formatAmountForCurrency(amount: number, currency: string): string {
    return amount.toFixed(getCurrencyDecimals(currency));
  }

  /**
   * Parse and set form data from expense for edit mode
   */
  parseExpenseForEdit(expenseData: unknown, currency: string) {
    const expense = expenseData as {
      amount?: number | string;
      title?: string;
      category?: string;
      subcategory?: string;
      date?: string;
      splitMethod?: string;
      description?: string;
      notes?: string;
      originalAmount?: number;
      originalCurrency?: string;
      exchangeRate?: number;
      rateDate?: string;
      rateSource?: 'provider' | 'manual';
    };

    const amountValue =
      typeof expense.amount === 'number'
        ? expense.amount
        : normalizeDecimalInput(String(expense.amount || 0)) || 0;

    const isForeign =
      expense.originalAmount !== undefined &&
      expense.originalCurrency !== undefined;

    return {
      title: expense.title || '',
      amount: this.formatAmountForCurrency(amountValue, currency),
      numericAmount: amountValue,
      category: expense.category || 'other',
      subcategory: expense.subcategory || undefined,
      date: expense.date
        ? this.getLocalDateString(parseDate(expense.date))
        : this.getLocalDateString(),
      splitMethod: expense.splitMethod || 'equal',
      notes: expense.description || expense.notes || '',
      enteredAmount: isForeign ? expense.originalAmount! : amountValue,
      enteredCurrency: isForeign ? expense.originalCurrency! : currency,
      ...(isForeign
        ? storedRateForEdit(expense)
        : { frozen: null, manualRate: null }),
    };
  }
}

export const expenseFormService = new ExpenseFormService();
